import {LoginRequest, UpdateTwoFa, RequestRegistration, ProductType, LoginMethodId} from "platformed-client/types";
import {analyticsIdentifyAccount, analyticsIdentifyUser} from "../utils/analytics";
import {invalidateQueries, q, resetQueries} from "../state";
import {router} from "../router";
import {clearSessionState} from "../hooks/sessionState";
import {platformedClient} from ".";

export async function whoAmI(options = {handleAuthErrors: true}) {
  const res = await platformedClient.internalRequest("get", "/api/whoami", {
    skipErrorHandler: !options.handleAuthErrors,
  });
  if (res.user) {
    analyticsIdentifyUser(res.user);
  }
  return res;
}

export async function whoAmIOnAccount() {
  const res = await platformedClient.internalRequest("get", "/region/{region}/api/whoami_on_account", {});
  analyticsIdentifyAccount(res.account);
  return res;
}

export async function logout({allDevices = false, redirect}: {allDevices?: boolean; redirect?: string} = {}) {
  await rewritingLoginCookie(() =>
    platformedClient.internalRequest("post", "/api/logout", {query: {all_devices: allDevices}}),
  );
  clearSessionState();
  await resetQueries();
  await router!.navigate(redirect ? `/login?${new URLSearchParams({redirect})}` : "/login");
}

export async function preLogin(email: string) {
  const res = await platformedClient.internalRequest("post", "/api/pre_login", {body: email});
  return res;
}

export async function login(loginMethodId: LoginMethodId, body: LoginRequest) {
  const res = await rewritingLoginCookie(() =>
    platformedClient.internalRequest("post", "/api/login/methods/{login_method_id}/login", {
      body,
      path: {login_method_id: loginMethodId},
    }),
  );

  if (res.type === "Success") {
    await resetQueries();
  }

  return res;
}

export async function peekSignInLink(token: string) {
  return await platformedClient.internalRequest("get", "/api/login/sign_in_link", {
    header: {Authorization: `Bearer ${token}`},
    skipErrorHandler: true,
  });
}

export async function redeemSignInLink(token: string, rememberMe: boolean) {
  const res = await rewritingLoginCookie(() =>
    platformedClient.internalRequest("post", "/api/login/sign_in_link", {
      body: {remember_me: rememberMe},
      header: {Authorization: `Bearer ${token}`},
      skipErrorHandler: true,
    }),
  );
  await resetQueries();
  return res;
}

export async function register(body: RequestRegistration) {
  return await platformedClient.internalRequest("post", "/api/register", {body});
}

export async function changeName(name: string) {
  const res = await platformedClient.internalRequest("post", "/api/login/name", {body: name});
  await invalidateQueries([q.whoAmI.filter(), q.user.filter(), q.accountUsers.filter()]);
  return res;
}

export async function changeLanguage(language: string) {
  const res = await platformedClient.internalRequest("post", "/api/login/language", {body: language});
  await invalidateQueries([q.whoAmI.filter(), q.user.filter(), q.accountUsers.filter()]);
  return res;
}

export async function listAccounts() {
  return await platformedClient.internalRequest("get", "/api/login/accounts", {});
}

export async function generateTotp() {
  return await platformedClient.internalRequest("get", "/api/login/2fa/totp", {});
}

export async function generateTotpOtt(token: string, name: string) {
  return await platformedClient.internalRequest("post", "/api/login/2fa/totp_ott", {
    body: name,
    skipErrorHandler: true,
    header: {Authorization: `Bearer ${token}`},
  });
}

export async function update2FA(update: UpdateTwoFa) {
  const res = await rewritingLoginCookie(() =>
    platformedClient.internalRequest("post", "/api/login/2fa", {body: update}),
  );
  await invalidateQueries([q.whoAmI.filter(), q.user.filter(), q.accountUsers.filter()]);
  return res;
}

export async function updateInternalMode(internalMode: boolean) {
  const res = await platformedClient.internalRequest("post", "/api/login/internal_mode", {body: internalMode});
  await resetQueries();
  return res;
}

export async function logSession({product}: {product: ProductType}) {
  const res = await platformedClient.internalRequest("post", "/api/session", {body: {product}});
  return res;
}

const refreshesInFlight = new Set<{abort: AbortController; settled: Promise<unknown>}>();
let cookieRewrites = 0;

/**
 * A refresh re-sends the login cookie, so one whose response lands after a
 * logout or a reissue would put back the cookie that request removed or
 * replaced. A refresh therefore never overlaps a request made through
 * `rewritingLoginCookie` in this tab.
 */
export async function refreshSession() {
  if (cookieRewrites > 0) {
    return;
  }
  const abort = new AbortController();
  const settled = platformedClient.internalRequest("post", "/api/refresh_session", {
    // The global handler's redirect to login works only during render.
    skipErrorHandler: true,
    signal: abort.signal,
  });
  const refresh = {abort, settled};
  refreshesInFlight.add(refresh);
  try {
    await settled;
  } finally {
    refreshesInFlight.delete(refresh);
  }
}

/**
 * Runs `request`, which sets, removes or replaces the login cookie, with no
 * refresh in flight. Refreshes in flight are aborted, so a stalled one cannot
 * hold up a logout: an abort settles at once, and the browser applies no
 * cookie from a response it has not yet received.
 */
export async function rewritingLoginCookie<T>(request: () => Promise<T>): Promise<T> {
  cookieRewrites++;
  try {
    const refreshes = [...refreshesInFlight];
    for (const refresh of refreshes) {
      refresh.abort.abort();
    }
    await Promise.allSettled(refreshes.map(refresh => refresh.settled));
    return await request();
  } finally {
    cookieRewrites--;
  }
}
